import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IPrediction,
	IPredictionApiProvider,
} from "data/providers/api/predictions.api.provider";
import {RequestState} from "data/enums";

export interface IPredictionsStore {
	get getIsLoading(): boolean;
	get prediction(): IPrediction;
	get savedPlayerId(): number;
	get isPredictionSaved(): boolean;
	get isReadyForUpdate(): boolean;

	getPredictionByContestId(contestId: number | undefined): IPrediction | undefined;
	setPrediction: (payload: IPrediction) => void;
	fetchAllPredictions(): Promise<void>;
	fetchPredictionByContest(contestId: number): Promise<void>;
	savePrediction(payload: IPrediction): Promise<void>;
	clearSavedState(): void;
}

@injectable()
export class PredictionsStore implements IPredictionsStore {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _prediction: IPrediction = {contestId: 0, playerId: 0};
	@observable private _predictions: IPrediction[] | null = null;
	@observable private _savedPlayerId: number = 0;
	@observable _isPredictionSaved: boolean = false;
	@observable _isReadyForUpdate: boolean = false;

	get prediction() {
		return this._prediction;
	}
	get savedPlayerId() {
		return this._savedPlayerId;
	}

	get getIsLoading(): boolean {
		return this._requestState === RequestState.PENDING;
	}

	get isPredictionSaved() {
		return this._isPredictionSaved;
	}
	get isReadyForUpdate() {
		return this._isReadyForUpdate;
	}

	@action getPredictionByContestId(contestId: number | undefined) {
		return this._predictions?.find((prediction) => prediction.contestId === contestId);
	}

	constructor(
		@inject(Bindings.PredictionApiProvider)
		private _predictionApiProvider: IPredictionApiProvider
	) {
		makeAutoObservable(this);
	}

	@action clearSavedState() {
		this._isReadyForUpdate = false;
		this._isPredictionSaved = false;
	}

	@action setPrediction = (payload: IPrediction) => {
		if (this._prediction.playerId === payload.playerId) return;

		this._prediction = payload;

		if (this._savedPlayerId === payload.playerId) {
			this._isReadyForUpdate = false;
			return;
		}

		if (this._isPredictionSaved) {
			this._isReadyForUpdate = true;
		}
	};

	@action
	async fetchAllPredictions() {
		const {data} = await this._predictionApiProvider.predictions();

		runInAction(() => {
			this._predictions = data.success.predictions;
		});
	}

	@action
	async fetchPredictionByContest(contestId: number) {
		this._requestState = RequestState.PENDING;
		const {data} = await this._predictionApiProvider.get(contestId);

		runInAction(() => {
			this._prediction = data.success.prediction;
			this._savedPlayerId = data.success.prediction.playerId;
			if (this._prediction.playerId) {
				this._isPredictionSaved = true;
			}

			this._requestState = RequestState.SUCCESS;
		});
	}

	@action async savePrediction(payload: IPrediction) {
		this._requestState = RequestState.PENDING;
		const {data} = await this._predictionApiProvider.save(payload);

		runInAction(() => {
			this._prediction = data.success.prediction;
			this._savedPlayerId = data.success.prediction.playerId;
			this._isPredictionSaved = true;
			this._isReadyForUpdate = false;

			this._requestState = RequestState.SUCCESS;
		});
	}
}
